import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TextInput,
    TouchableOpacity,
    ActivityIndicator
} from 'react-native';
import moment from 'moment';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';
import DataTable from "react-data-table-component";
import Modal from "react-native-modal";
import { sensorState, useScale, ScaleFn, TablePagination , useDarkMode} from './common';
import Data, { authHeader } from './Data';
import { getStorage, setStorage } from '../storage';
import { createPortal } from 'react-dom';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};
type Theme = typeof DARK;

const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

const OpenModal = ({ modalVisible, setModalVisible, T }: any) => {
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [text, setText] = useState('');
    const [id, setID] = useState('');

    const onChangeText = (text: any) => { setText(text) };
    const onChangeRoom = (text: any) => { setID(text) };

    return (
        <Modal
            style={{ flex: 1 }}
            backdropColor="black"
            isVisible={modalVisible}
            backdropOpacity={0.7}
            onBackdropPress={() => { setModalVisible(false); }}
        >
            <View style={[styles.modalView, { backgroundColor: T.card, borderColor: T.border }]}>
                <View style={[styles.modalHeader, { borderColor: T.border }]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Text style={[styles.modalTitle, { color: T.text }]}>센서 등록</Text>
                    </View>
                    <TouchableOpacity onPress={() => setModalVisible(false)}>
                        <AntDesign name='close' size={s(28)} color={T.textSub} />
                    </TouchableOpacity>
                </View>
                <View style={styles.box}>
                    <View style={styles.rowstyle}>
                        <Text style={[styles.contentFont, { color: T.text }]}>중계기 ID</Text>
                        <View style={[styles.textInputBox, { borderColor: T.border, backgroundColor: T.cardInner }]}>
                            <TextInput autoCapitalize='none' clearButtonMode='always'
                                style={[styles.textInput, { color: T.text }]} onChangeText={(text: any) => { onChangeRoom(text) }} value={id}
                                placeholderTextColor={T.muted} />
                        </View>
                    </View>
                    <View style={styles.rowstyle}>
                        <Text style={[styles.contentFont, { color: T.text }]}>시리얼 넘버</Text>
                        <View style={[styles.textInputBox, { borderColor: T.border, backgroundColor: T.cardInner }]}>
                            <TextInput autoCapitalize='none' clearButtonMode='always'
                                style={[styles.textInput, { color: T.text }]} onChangeText={(text: any) => { onChangeText(text) }} value={text}
                                placeholderTextColor={T.muted} />
                        </View>
                    </View>
                </View>
                <View style={styles.buttonBox}>
                    <TouchableOpacity style={styles.modifyButton}
                        onPress={() => { alert('등록되었습니다.'); setModalVisible(!modalVisible); }} >
                        <Text style={styles.buttonFont}>등록</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[styles.modifyButton, { backgroundColor: "#1890FF", }]}
                        onPress={() => { alert('취소되었습니다.'); setModalVisible(!modalVisible) }} >
                        <Text style={styles.buttonFont}>취소</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    )
};

const sensorColumns = [
    {
        id: 1,
        name: "no.",
        selector: (row: any, index: any) => index + 1,
        grow: 0
    },
    {
        id: 2,
        name: "이름",
        selector: (row: any) => row.NAME,
    },
    {
        id: 3,
        name: '성별',
        selector: (row: any) => row.GENDER,
    },
    {
        id: 4,
        name: '생년월일',
        selector: (row: any) => row.BIRTH ? moment(row.BIRTH).format('YYYY-MM-DD') : '',
    },
    {
        id: 5,
        name: '병실 호수',
        selector: (row: any) => row.ROOM_NO,
    },
    {
        id: 6,
        name: "침대 no.",
        selector: (row: any) => row.BED_NO ? row.BED_NO : '',
    },
    {
        id: 7,
        name: '센서 ID',
        selector: (row: any) => row.SENSOR_ID,
    },
    {
        id: 8,
        name: '연결 상태',
        selector: (row: any) => sensorState(row),
    },
];

export default function Sensor({ navigation }: any) {
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [modalVisible, setModalVisible] = useState(false);
    const [list, setList] = useState<any[]>([]);
    const [filteredList, setFilteredList] = useState<any>([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const _totalPages = Math.max(1, Math.ceil(((filteredList) || []).length / perPage));
    const _curPage = Math.min(Math.max(1, page), _totalPages);
    const pageData = ((filteredList) || []).slice((_curPage - 1) * perPage, _curPage * perPage);
    const [searchText, setSearchText] = useState('');
    const { dark, setDark } = useDarkMode();
    const [admin, setAdmin] = useState('1');
    const [excelLoading, setExcelLoading] = useState(false);
    const [excelOpen, setExcelOpen] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState('');
    const dropdownRef = useRef<any>(null);
    const portalRef = useRef<any>(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
        if (!excelOpen) return;
        const handler = (e: any) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                portalRef.current && !portalRef.current.contains(e.target)) {
                setExcelOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [excelOpen]);

    const openDropdown = () => {
        if (excelLoading) return;
        if (dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 180) });
        }
        setExcelOpen(!excelOpen);
    };

    useEffect(() => {
        if (!isFocused) return;
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    const tableStyles = {
        headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
        headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
        rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner } } },
        cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
        pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
            pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
        noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
    };

    useEffect(() => {
        if (!isFocused) return;
        const load = async () => {
            const userInfo = await getStorage('AutoLogin');
            const adminValue = userInfo?.admin ?? '1';
            setAdmin(adminValue);

            Data.fetchData('sensor/getSensorAllListbyUser', {}, (result: any, count: any) => {
                if (count > 0) {
                    setList(result);
                } else {
                    setList([]);
                }
            }, true);
        };
        load();
    }, [isFocused]);

    // 검색 필터링 로직
    useEffect(() => {
        if (!list) return;
        if (searchText === '') {
            setFilteredList(list);
        } else {
            const filtered = list.filter((item: any) =>
                item?.NAME?.includes(searchText) || item?.ROOM_NO?.includes(searchText)
            );
            setFilteredList(filtered);
        }
    }, [searchText, list]);

    const downloadAllExcel = async (month: string) => {
        if (excelLoading) return;
        setExcelLoading(true);
        try {
            const response = await fetch('sensor/allSensorsMonthlyExcel', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
                body: JSON.stringify({ month }),
            });
            if (!response.ok) {
                const msg = await response.text();
                alert(msg || '다운로드 실패');
                return;
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `전체센서_${month}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('전체 엑셀 다운로드 실패:', err);
            alert('다운로드 중 오류가 발생했습니다.');
        } finally {
            setExcelLoading(false);
        }
    };

    return (
        <View style={[styles.container, { backgroundColor: T.bg }]}>
            <View style={[styles.dataTable, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                {/* ─── 헤더 ─── */}
                <View style={styles.TopBox}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>센서 목록</Text>
                        {filteredList?.length > 0 && (
                            <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{filteredList.length}</Text>
                            </View>
                        )}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        {/* 월 선택 드롭다운 */}
                        <View ref={dropdownRef}>
                            <TouchableOpacity
                                style={[styles.excelDropdown, { borderColor: T.border, backgroundColor: T.cardInner }]}
                                onPress={openDropdown}
                                disabled={excelLoading}
                            >
                                <Ionicons name="calendar-outline" size={s(15)} color={T.textSub} style={{ marginRight: s(6) }} />
                                <Text style={{ flex: 1, fontSize: s(13), fontWeight: '600', color: selectedMonth ? T.text : T.muted }}>
                                    {selectedMonth ? moment(selectedMonth, 'YYYY-MM').format('YYYY년 MM월') : '월 선택'}
                                </Text>
                                <AntDesign name={excelOpen ? 'up' : 'down'} size={s(12)} color={T.muted} style={{ marginLeft: s(4) }} />
                            </TouchableOpacity>
                            {excelOpen && createPortal(
                                <div ref={portalRef} style={{
                                    position: 'fixed', top: dropdownPos.top, left: dropdownPos.left,
                                    width: dropdownPos.width, zIndex: 99999,
                                    backgroundColor: dark ? '#1E293B' : '#FFFFFF',
                                    border: `1px solid ${dark ? 'rgba(99,179,237,0.12)' : '#E5E7EB'}`,
                                    borderRadius: s(10), overflow: 'hidden',
                                    boxShadow: dark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.12)',
                                    maxHeight: s(280), overflowY: 'auto',
                                }}>
                                    {Array.from({ length: 12 }, (_, i) => {
                                        const m = moment().subtract(i, 'months');
                                        const isSelected = selectedMonth === m.format('YYYY-MM');
                                        return (
                                            <div
                                                key={m.format('YYYY-MM')}
                                                onClick={() => { setSelectedMonth(m.format('YYYY-MM')); setExcelOpen(false); }}
                                                style={{
                                                    padding: '10px 14px', cursor: 'pointer',
                                                    borderBottom: `0.5px solid ${dark ? 'rgba(99,179,237,0.12)' : '#E5E7EB'}`,
                                                    color: isSelected ? '#38BDF8' : (dark ? '#E2E8F0' : '#111827'),
                                                    fontSize: s(13), fontWeight: isSelected ? '700' : '400',
                                                    backgroundColor: isSelected ? (dark ? 'rgba(56,189,248,0.1)' : 'rgba(56,189,248,0.08)') : 'transparent',
                                                }}
                                                onMouseEnter={(e: any) => { if (!isSelected) e.currentTarget.style.backgroundColor = dark ? '#0F172A' : '#F3F4F6'; }}
                                                onMouseLeave={(e: any) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                            >
                                                {m.format('YYYY년 MM월')}
                                            </div>
                                        );
                                    })}
                                </div>,
                                document.body
                            )}
                        </View>
                        {/* 다운로드 버튼 */}
                        <TouchableOpacity
                            style={[styles.downloadBtn, {
                                borderColor: T.border,
                                backgroundColor: T.cardInner,
                                opacity: selectedMonth && !excelLoading ? 1 : 0.4,
                            }]}
                            onPress={() => { if (selectedMonth && !excelLoading) downloadAllExcel(selectedMonth); }}
                            disabled={!selectedMonth || excelLoading}
                        >
                            {excelLoading
                                ? <ActivityIndicator size={s(14)} color={T.textSub} style={{ marginRight: s(4) }} />
                                : <Ionicons name="download-outline" size={s(14)} color={T.textSub} style={{ marginRight: s(4) }} />
                            }
                            <Text style={{ fontSize: s(12), fontWeight: '600', color: T.textSub }}>
                                {excelLoading ? '처리중...' : '다운로드'}
                            </Text>
                        </TouchableOpacity>

                        <View style={[styles.searchContainer, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                            <Ionicons name="search-sharp" size={s(18)} color={T.muted} style={{ marginRight: s(8) }} />
                            <TextInput
                                style={[styles.searchBox, { color: T.text }]}
                                placeholder="이름, 병실 검색..."
                                placeholderTextColor={T.muted}
                                value={searchText}
                                onChangeText={(text) => setSearchText(text)}
                            />
                            {searchText !== '' && (
                                <TouchableOpacity onPress={() => setSearchText('')} style={{ padding: s(2) }}>
                                    <AntDesign name="closecircle" size={s(14)} color={T.muted} />
                                </TouchableOpacity>
                            )}
                        </View>
                        <TouchableOpacity
                            style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                            onPress={() => { navigation.navigate('Setting') }}
                        >
                            <Ionicons name="settings-sharp" size={s(16)} color={T.textSub} />
                        </TouchableOpacity>
                    </View>
                </View>
                {/* ─── 테이블 ─── */}
                <DataTable
                    columns={sensorColumns}
                    data={pageData}
                    customStyles={tableStyles}
                    onRowClicked={(row: any) => {
                        try { window.localStorage.setItem(`_navParams_AnalysisDetail_${row.SENSOR_ID}`, JSON.stringify({ data: row })); } catch {}
                        navigation.navigate('AnalysisDetail', { id: String(row.SENSOR_ID) });
                    }}
                />
                {(filteredList || []).length > 0 && (
                    <TablePagination
                        rowCount={(filteredList || []).length}
                        perPage={perPage}
                        page={_curPage}
                        onChangePage={setPage}
                        onChangePerPage={(n) => { setPerPage(n); setPage(1); }}
                        T={T}
                        s={s}
                        accent={S.accent}
                    />
                )}
            </View>
            <OpenModal modalVisible={modalVisible} setModalVisible={setModalVisible} T={T} />
        </View>
    );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
    container: {
        flex: 1,
        padding: s(24),
    },
    TopBox: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: s(6),
        paddingBottom: s(12),
    },
    iconBtn: {
        width: s(34),
        height: s(34),
        borderRadius: s(10),
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonBox: {
        marginTop: s(20),
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'center'
    },
    excelDropdown: {
        flexDirection: 'row',
        alignItems: 'center',
        height: s(36),
        borderWidth: 1,
        borderRadius: s(10),
        paddingHorizontal: s(10),
        minWidth: s(160),
    },
    downloadBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        height: s(34),
        borderRadius: s(10),
        borderWidth: 1,
        paddingHorizontal: s(10),
    },
    dropdownList: {
        position: 'absolute',
        top: s(40),
        left: 0,
        right: 0,
        zIndex: 9999,
        borderWidth: 1,
        borderRadius: s(10),
        overflow: 'hidden',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: s(12),
        elevation: 8,
        maxHeight: s(300),
    },
    dropdownItem: {
        paddingVertical: s(10),
        paddingHorizontal: s(14),
        borderBottomWidth: 0.5,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        height: s(36),
        borderWidth: 1,
        borderRadius: s(10),
        paddingHorizontal: s(12),
        width: s(240),
    },
    searchBox: {
        flex: 1,
        fontSize: s(14),
        outlineStyle: 'none',
    },
    modifyButton: {
        paddingHorizontal: s(20),
        paddingVertical: s(6),
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 0,
        borderRadius: s(10),
        backgroundColor: '#FF8A80',
        marginRight: s(20)
    },
    buttonFont: {
        fontSize: s(16),
        fontWeight: '600',
        color: '#fff'
    },
    modalView: {
        margin: s(20),
        borderWidth: 1,
        borderRadius: s(20),
        padding: s(35),
        alignSelf: 'center',
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 2
        },
        shadowOpacity: 0.25,
        shadowRadius: s(4),
        elevation: 5,
        width: '60%',
    },
    modalHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: s(10),
        marginBottom: s(10),
        borderBottomWidth: 3,
    },
    modalTitle: {
        fontSize: s(22),
        fontWeight: '700',
        textAlign: 'center',
        marginRight: s(20)
    },
    box: {
        width: '100%',
        borderWidth: 0,
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: s(10)
    },
    rowstyle: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: s(5),
        width: '70%',
        borderWidth: 0,
        justifyContent: 'space-between'
    },
    contentFont: {
        fontSize: s(16),
        fontWeight: '600',
    },
    textInputBox: {
        borderWidth: 1,
        width: '50%',
        height: s(30),
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: s(6),
    },
    textInput: {
        fontSize: s(18),
        width: '100%',
        height: '100%',
        paddingHorizontal: s(10)
    },
    dataTable: {
        borderRadius: s(16),
        borderWidth: 0.5,
        overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: s(20),
        elevation: 3,
        paddingTop: s(20),
        paddingHorizontal: s(10),
    },
});
